import { useStore } from '@/store/useStore';
import {
  getAmplitudeProduct,
  getAndroidAppVersion,
  getAndroidDistribution,
  toLoginStatus,
} from './helpers';
import {
  AMPLITUDE_EVENTS,
  MODULES,
  TRACK_USER_ACTION_EVENT,
  ZONES,
  getAmplitudeKey,
} from './constants';

const moduleValue = (key: keyof typeof MODULES) => MODULES[key];
const zoneValue = (key: keyof typeof ZONES) => ZONES[key];

export const buildEventPayload = (params: Record<string, unknown>) => {
  switch (params.event) {
    case TRACK_USER_ACTION_EVENT.SCREEN_LOAD: {
      const state = useStore.getState();
      const accountId = state.userContext.data?.accountSettings?.accountID;
      const hasActiveSession = state.userSessionContext.hasActiveSession;
      const userId = state.userSessionContext.userId;
      const region = state.runtime.region;
      const brand = state.runtime.brand;
      const deviceType = state.runtime.deviceType;

      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.SCREEN_LOAD,
        [getAmplitudeKey('USER_ID')]: userId ?? '-',
        [getAmplitudeKey('ACCOUNT_ID')]: accountId,
        [getAmplitudeKey('ANDROID_DISTRIBUTION_METHOD')]:
          getAndroidDistribution(),
        [getAmplitudeKey('LOGIN_STATUS')]: toLoginStatus(hasActiveSession),
        [getAmplitudeKey('BRAND')]: brand,
        [getAmplitudeKey('JURISDICTION')]: region ? region.toUpperCase() : '-',
        [getAmplitudeKey('ORIENTATION')]: deviceType,
        [getAmplitudeKey('APP_VERSION')]: getAndroidAppVersion(),
        [getAmplitudeKey('PRODUCT')]: getAmplitudeProduct() ?? '-',
      };
    }
    case TRACK_USER_ACTION_EVENT.IN_GAME_EXITED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_EXITED,
        [getAmplitudeKey('MODULE')]: moduleValue(
          params.module as keyof typeof MODULES,
        ),
        [getAmplitudeKey('ZONE_NAME')]: zoneValue(
          params.zone as keyof typeof ZONES,
        ),
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_EXIT_MODAL_VIEWED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_EXIT_MODAL_VIEWED,
        [getAmplitudeKey('MODULE')]: MODULES.GW_EXIT_MODAL,
        [getAmplitudeKey('ZONE_NAME')]: zoneValue(
          params.zone as keyof typeof ZONES,
        ),
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_EXIT_MODAL_CLOSED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_EXIT_MODAL_CLOSED,
        [getAmplitudeKey('MODULE')]: MODULES.GW_EXIT_MODAL,
        [getAmplitudeKey('ZONE_NAME')]: zoneValue(
          params.zone as keyof typeof ZONES,
        ),
        [getAmplitudeKey('LINK_TEXT')]: params.linkText,
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_MENU_OPENED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_MENU_OPENED,
        [getAmplitudeKey('MODULE')]: MODULES.GW,
        [getAmplitudeKey('ZONE_NAME')]: zoneValue(
          params.zone as keyof typeof ZONES,
        ),
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_SOUND_ADJUSTED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_SOUND_ADJUSTED,
        [getAmplitudeKey('MODULE')]: MODULES.IN_GAME_HEADER,
        [getAmplitudeKey('ZONE_NAME')]: zoneValue(
          params.zone as keyof typeof ZONES,
        ),
        [getAmplitudeKey('INTERACTION_TYPE')]: params.interactionType,
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_ZONE_VIEWED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_ZONE_VIEWED,
        [getAmplitudeKey('MODULE')]: MODULES.IN_GAME,
        [getAmplitudeKey('ZONE_NAME')]: zoneValue(
          params.zone as keyof typeof ZONES,
        ),
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_CTA_CLICKED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_CTA_CLICKED,
        [getAmplitudeKey('MODULE')]: moduleValue(
          params.module as keyof typeof MODULES,
        ),
        [getAmplitudeKey('ZONE_NAME')]: zoneValue(
          params.zone as keyof typeof ZONES,
        ),
        [getAmplitudeKey('LINK_URL')]: params.linkUrl,
        [getAmplitudeKey('LINK_TEXT')]: params.linkText,
        ...(params.position !== undefined
          ? { [getAmplitudeKey('POSITION')]: params.position }
          : {}),
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_ERROR_MODAL_VIEWED:
      if (params.optedIn) {
        return {
          [getAmplitudeKey('EVENT')]:
            AMPLITUDE_EVENTS.IN_GAME_ERROR_MODAL_VIEWED,
          [getAmplitudeKey('MODULE')]: MODULES.GW_ERROR,
          [getAmplitudeKey('PAGE_NAME')]: params.pageName
            ? moduleValue(params.pageName as keyof typeof MODULES)
            : MODULES.GW,
          [getAmplitudeKey('ERROR_TYPE')]: params.errorType,
        };
      } else
        return {
          [getAmplitudeKey('EVENT')]:
            AMPLITUDE_EVENTS.IN_GAME_ERROR_MODAL_VIEWED,
          [getAmplitudeKey('MODULE')]: MODULES.GW_ERROR,
          [getAmplitudeKey('PAGE_NAME')]: params.pageName
            ? moduleValue(params.pageName as keyof typeof MODULES)
            : MODULES.GW,
          [getAmplitudeKey('ERROR_TYPE')]: params.errorType,
          [getAmplitudeKey('MULTIPLIER')]: params.multiplier,
        };
    case TRACK_USER_ACTION_EVENT.IN_GAME_ERROR_CTA_CLICKED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_ERROR_CTA_CLICKED,
        [getAmplitudeKey('MODULE')]: MODULES.GW_ERROR,
        [getAmplitudeKey('ZONE_NAME')]: zoneValue(
          params.zone as keyof typeof ZONES,
        ),
        [getAmplitudeKey('LINK_TEXT')]: params.linkText,
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_NOTIFICATION_VIEWED:
      return {
        [getAmplitudeKey('EVENT')]:
          AMPLITUDE_EVENTS.IN_GAME_NOTIFICATION_VIEWED,
        [getAmplitudeKey('MODULE')]: MODULES.FEATURE_NOTIFICATION,
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_NOTIFICATION_SELECTED:
      return {
        [getAmplitudeKey('EVENT')]:
          AMPLITUDE_EVENTS.IN_GAME_NOTIFICATION_SELECTED,
        [getAmplitudeKey('MODULE')]: MODULES.FEATURE_NOTIFICATION,
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_NOTIFICATION_CLOSED:
      return {
        [getAmplitudeKey('EVENT')]:
          AMPLITUDE_EVENTS.IN_GAME_NOTIFICATION_CLOSED,
        [getAmplitudeKey('MODULE')]: MODULES.FEATURE_NOTIFICATION,
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_JACKPOT_WIN:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_JACKPOT_WIN,
        [getAmplitudeKey('MODULE')]: MODULES.JACKPOT_WIN,
        [getAmplitudeKey('FDC_JACKPOT_WON_TIER')]: params.tierName,
        [getAmplitudeKey('FDC_JACKPOT_WON_AMOUNT')]: params.amount,
        [getAmplitudeKey('FDC_JACKPOT_MULTIPLIER')]: params.multiplier,
        [getAmplitudeKey('FDC_JACKPOT_OPT_IN_STATUS')]: params.optInStatus,
        [getAmplitudeKey('PAGE_NAME')]: 'game_window',
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_JACKPOTS_MODAL_TOGGLED:
      return {
        [getAmplitudeKey('EVENT')]:
          `FDC Jackpots Modal ${!params.showTCPopup ? 'Opened' : 'Closed'}`,
        [getAmplitudeKey('FDC_JACKPOT_OPT_IN_STATUS')]:
          `opted_${params.optedInOption}`,
        [getAmplitudeKey('MODULE')]: MODULES.GW,
        [getAmplitudeKey('PAGE_NAME')]: 'game_window',
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_JACKPOTS_MODAL_VIEWED:
      return {
        [getAmplitudeKey('EVENT')]:
          AMPLITUDE_EVENTS.IN_GAME_JACKPOT_MODAL_VIEWED,
        [getAmplitudeKey('INTERACTION_TYPE')]: 'opened',
        [getAmplitudeKey('FDC_JACKPOT_OPT_IN_STATUS')]:
          `opted_${params.optedInOption}`,
        [getAmplitudeKey('MODULE')]: MODULES.GW,
        [getAmplitudeKey('PAGE_NAME')]: 'game_window',
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_JACKPOTS_OPT_IN_TOGGLED:
      return {
        [getAmplitudeKey('EVENT')]: `FDC Jackpots Opt In ${
          params.optedInOption === 'in' &&
          (!params.multiplier || params.multiplier === 0)
            ? 'Off'
            : 'On'
        } Toggled`,
        [getAmplitudeKey('FDC_JACKPOT_OPT_IN_STATUS')]:
          `opted_${params.optedInOption}`,
        ...(params.isDYB
          ? {
              [getAmplitudeKey('FDC_JACKPOT_MULTIPLIER')]: params.multiplier
                ? `${params.multiplier}x`
                : null,
            }
          : {}),
        [getAmplitudeKey('FDC_JACKPOT_MINI_POT_AMOUNT')]:
          params.jackpotTiers?.['Mini']?.currentAmount,
        [getAmplitudeKey('FDC_JACKPOT_MINOR_POT_AMOUNT')]:
          params.jackpotTiers?.['Minor']?.currentAmount,
        [getAmplitudeKey('FDC_JACKPOT_MAJOR_POT_AMOUNT')]:
          params.jackpotTiers?.['Major']?.currentAmount,
        [getAmplitudeKey('FDC_JACKPOT_MEGA_POT_AMOUNT')]:
          params.jackpotTiers?.['Mega']?.currentAmount,
        [getAmplitudeKey('MODULE')]: MODULES.GW,
        [getAmplitudeKey('PAGE_NAME')]: 'game_window',
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_SCREEN_TOGGLED:
      return {
        [getAmplitudeKey('EVENT')]:
          `Game Screen ${!params.isOn ? 'Minimized' : 'Maximized'}`,
        [getAmplitudeKey('FDC_JACKPOT_OPT_IN_STATUS')]:
          `opted_${params.optedInOption}`,
        [getAmplitudeKey('MODULE')]: MODULES.GW,
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_WIN_SCREEN_CTA_CLICKED:
      return {
        [getAmplitudeKey('EVENT')]:
          AMPLITUDE_EVENTS.IN_GAME_JACKPOT_WIN_PAGE_CTA_CLICKED,
        [getAmplitudeKey('FDC_JACKPOT_OPT_IN_STATUS')]:
          `opted_${params.optedInOption}`,
        [getAmplitudeKey('FDC_JACKPOT_WON_TIER')]: params.tier,
        [getAmplitudeKey('FDC_JACKPOT_WON_AMOUNT')]: params.amount,
        [getAmplitudeKey('LINK_TEXT')]: params.linkText,
      };
    case TRACK_USER_ACTION_EVENT.IN_GAME_WIN_SCREEN_VIEWED:
      return {
        [getAmplitudeKey('EVENT')]: AMPLITUDE_EVENTS.IN_GAME_WIN_SCREEN_VIEWED,
        [getAmplitudeKey('FDC_JACKPOT_OPT_IN_STATUS')]:
          `opted_${params.optedInOption}`,
        [getAmplitudeKey('FDC_JACKPOT_WON_TIER')]: params.tier,
        [getAmplitudeKey('FDC_JACKPOT_WON_AMOUNT')]: params.amount,
        [getAmplitudeKey('PAGE_NAME')]: 'fdc_jackpots_win',
        [getAmplitudeKey('MODULE')]: MODULES.GW,
      };
    default:
      return params;
  }
};
